(function (global) {
  'use strict';

  var DEFAULT_USABLE_LOAD_PERCENT = 80;

  function number(value) {
    var parsed = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function booleanValue(value) {
    if (value === true || value === 'true' || value === 1 || value === '1') return true;
    if (value === false || value === 'false' || value === 0 || value === '0') return false;
    return null;
  }

  function round(value, places) {
    var factor = Math.pow(10, places || 0);
    return Math.round((value + Number.EPSILON) * factor) / factor;
  }

  function effectiveCapacity(driver, fallbackPercent) {
    var usableLoadWatts = number(driver.usableLoadWatts);
    if (usableLoadWatts !== null && usableLoadWatts > 0) return usableLoadWatts;

    var ratedWattage = number(driver.ratedWattage);
    if (ratedWattage === null || ratedWattage <= 0) return null;

    var driverPercent = number(driver.usableLoadPercentage);
    var defaultPercent = number(fallbackPercent);
    var percent = driverPercent !== null && driverPercent > 0 && driverPercent <= 100
      ? driverPercent
      : (defaultPercent !== null && defaultPercent > 0 && defaultPercent <= 100 ? defaultPercent : DEFAULT_USABLE_LOAD_PERCENT);

    return ratedWattage * (percent / 100);
  }

  function calculate(options) {
    options = options || {};
    var tape = options.tape || {};
    var wattsPerMetre = number(tape.wattsPerMetre);
    var tapeVoltage = number(tape.voltage);
    var maxRunLength = number(tape.maxRunLength);
    var requestedDimmable = booleanValue(options.dimmable);
    var runs = Array.isArray(options.runs) ? options.runs : [];
    var validRunItems = runs.map(function (run, index) {
      return { run: number(run), index: index };
    }).filter(function (item) { return item.run !== null && item.run > 0; });
    var validRuns = validRunItems.map(function (item) { return item.run; });
    var totalMetres = validRuns.reduce(function (total, run) { return total + run; }, 0);

    if (wattsPerMetre === null || wattsPerMetre <= 0 || tapeVoltage === null || tapeVoltage <= 0) {
      return { status: 'invalid-tape', totalMetres: totalMetres, totalLoadWatts: 0, driver: null, longRuns: [] };
    }

    var totalLoadWatts = totalMetres * wattsPerMetre;
    var longRuns = maxRunLength !== null && maxRunLength > 0
      ? validRunItems.filter(function (item) { return item.run > maxRunLength; })
      : [];

    if (validRuns.length === 0) {
      return { status: 'empty', totalMetres: 0, totalLoadWatts: 0, driver: null, longRuns: [] };
    }

    var compatibleDrivers = (options.drivers || []).filter(function (driver) {
      var driverVoltage = number(driver.voltage);
      var driverDimmable = booleanValue(driver.dimmable);
      return booleanValue(driver.enabled) === true
        && driverVoltage !== null
        && driverVoltage === tapeVoltage
        && requestedDimmable !== null
        && driverDimmable === requestedDimmable;
    }).map(function (driver) {
      var copy = Object.assign({}, driver);
      copy.effectiveCapacityWatts = effectiveCapacity(driver, options.defaultUsableLoadPercentage);
      return copy;
    }).filter(function (driver) {
      return driver.effectiveCapacityWatts !== null && driver.effectiveCapacityWatts + 0.000001 >= totalLoadWatts;
    }).sort(function (a, b) {
      if (a.effectiveCapacityWatts !== b.effectiveCapacityWatts) return a.effectiveCapacityWatts - b.effectiveCapacityWatts;
      var priorityA = number(a.priority);
      var priorityB = number(b.priority);
      priorityA = priorityA === null ? 999999 : priorityA;
      priorityB = priorityB === null ? 999999 : priorityB;
      if (priorityA !== priorityB) return priorityA - priorityB;
      return (number(a.ratedWattage) || 0) - (number(b.ratedWattage) || 0);
    });

    return {
      status: compatibleDrivers.length ? 'matched' : 'no-match',
      totalMetres: round(totalMetres, 2),
      totalLoadWatts: round(totalLoadWatts, 2),
      driver: compatibleDrivers[0] || null,
      longRuns: longRuns,
      maxRunLength: maxRunLength
    };
  }

  var Engine = { DEFAULT_USABLE_LOAD_PERCENT: DEFAULT_USABLE_LOAD_PERCENT, calculate: calculate, effectiveCapacity: effectiveCapacity };
  global.RSLedDriverCalculator = Engine;
  if (typeof module !== 'undefined' && module.exports) module.exports = Engine;
  if (typeof document === 'undefined' || typeof customElements === 'undefined') return;

  function formatNumber(value) {
    return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(value);
  }

  class RSLedDriverCalculatorElement extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.data = this.readData();
      this.tape = this.data.tapes && this.data.tapes[0];
      this.runList = this.querySelector('[data-rs-led-runs]');
      this.runTemplate = this.querySelector('template[data-rs-led-run-template]');
      this.bindEvents();
      this.addRun();
    }

    readData() {
      var script = this.querySelector('script[data-rs-led-data]');
      if (!script) return { tapes: [], drivers: [] };
      try { return JSON.parse(script.textContent); }
      catch (error) { return { tapes: [], drivers: [], dataError: true }; }
    }

    bindEvents() {
      this.addEventListener('input', function (event) {
        if (event.target.matches('[data-rs-led-length]')) this.update();
      }.bind(this));
      this.addEventListener('change', function (event) {
        if (event.target.matches('[data-rs-led-dimmable]')) this.update();
      }.bind(this));
      this.addEventListener('click', function (event) {
        if (event.target.closest('[data-rs-led-add-run]')) { this.addRun(); return; }
        var removeButton = event.target.closest('[data-rs-led-remove-run]');
        if (removeButton) {
          var row = removeButton.closest('[data-rs-led-run]');
          if (row && this.runList.children.length > 1) row.remove();
          this.renumberRuns();
          this.update();
        }
      }.bind(this));

      var addForm = this.querySelector('[data-rs-led-add-form]');
      if (addForm) {
        addForm.addEventListener('submit', function (event) {
          event.preventDefault();
          var variantId = addForm.dataset.variantId || '';
          if (!/^\d+$/.test(variantId)) return;
          addForm.querySelectorAll('[data-rs-led-generated-input]').forEach(function (input) { input.remove(); });
          [['id', variantId], ['quantity', '1']].forEach(function (field) {
            var input = document.createElement('input');
            input.type = 'hidden';
            input.name = field[0];
            input.value = field[1];
            input.dataset.rsLedGeneratedInput = '';
            addForm.appendChild(input);
          });
          HTMLFormElement.prototype.submit.call(addForm);
        });
      }
    }

    addRun() {
      if (!this.runTemplate || !this.runList) return;
      this.runList.appendChild(this.runTemplate.content.cloneNode(true));
      this.renumberRuns();
      this.update();
      var inputs = this.runList.querySelectorAll('[data-rs-led-length]');
      if (inputs.length > 1) inputs[inputs.length - 1].focus();
    }

    renumberRuns() {
      var rows = this.runList.querySelectorAll('[data-rs-led-run]');
      rows.forEach(function (row, index) {
        var label = row.querySelector('[data-rs-led-run-label]');
        var input = row.querySelector('[data-rs-led-length]');
        var remove = row.querySelector('[data-rs-led-remove-run]');
        var numberValue = index + 1;
        if (label) label.textContent = this.dataset.runLabel + ' ' + numberValue;
        if (input) {
          input.id = this.id + '-run-' + numberValue;
          input.setAttribute('aria-label', this.dataset.runLabel + ' ' + numberValue + ' ' + this.dataset.metresLabel);
        }
        if (remove) {
          remove.disabled = rows.length === 1;
          remove.setAttribute('aria-label', this.dataset.removeLabel + ' ' + numberValue);
        }
      }.bind(this));
    }

    getRuns() {
      return Array.from(this.querySelectorAll('[data-rs-led-length]')).map(function (input) { return input.value; });
    }

    getDimmableChoice() {
      var selected = this.querySelector('[data-rs-led-dimmable]:checked');
      return selected ? booleanValue(selected.value) : false;
    }

    update() {
      var result = calculate({
        tape: this.tape,
        runs: this.getRuns(),
        drivers: this.data.drivers || [],
        dimmable: this.getDimmableChoice(),
        defaultUsableLoadPercentage: this.data.defaultUsableLoadPercentage
      });

      this.setText('[data-rs-led-total-metres]', result.totalMetres ? formatNumber(result.totalMetres) + ' m' : '—');
      this.setText('[data-rs-led-total-load]', result.totalLoadWatts ? formatNumber(result.totalLoadWatts) + ' W' : '—');
      var recommendation = this.querySelector('[data-rs-led-recommendation]');
      var empty = this.querySelector('[data-rs-led-empty]');
      var noMatch = this.querySelector('[data-rs-led-no-match]');
      var error = this.querySelector('[data-rs-led-error]');
      [recommendation, empty, noMatch, error].forEach(function (element) { if (element) element.hidden = true; });
      if (result.status === 'invalid-tape' || this.data.dataError) { if (error) error.hidden = false; }
      else if (result.status === 'empty') { if (empty) empty.hidden = false; }
      else if (result.status === 'no-match') { if (noMatch) noMatch.hidden = false; }
      else if (result.driver) { this.renderDriver(result.driver); if (recommendation) recommendation.hidden = false; }

      var maxRunWarning = this.querySelector('[data-rs-led-max-run-warning]');
      if (maxRunWarning) {
        maxRunWarning.hidden = result.longRuns.length === 0;
        if (result.longRuns.length) {
          var runNumbers = result.longRuns.map(function (item) { return item.index + 1; }).join(', ');
          maxRunWarning.textContent = this.dataset.maxRunPrefix + ' ' + runNumbers + ' ' + this.dataset.maxRunSuffix + ' ' + formatNumber(result.maxRunLength) + ' m.';
        }
      }
    }

    renderDriver(driver) {
      var driverCard = this.querySelector('[data-rs-led-driver]');
      if (driverCard) driverCard.classList.toggle('rs-led-driver-calculator__driver--no-image', !driver.image);
      this.setText('[data-rs-led-driver-title]', driver.title || this.dataset.driverFallbackLabel);
      this.setText('[data-rs-led-driver-capacity]', formatNumber(driver.ratedWattage) + ' W · ' + formatNumber(driver.voltage) + ' V');
      this.setText('[data-rs-led-driver-price]', driver.price || '');
      var image = this.querySelector('[data-rs-led-driver-image]');
      if (image) {
        image.hidden = !driver.image;
        if (driver.image) { image.src = driver.image; image.alt = driver.imageAlt || driver.title || ''; }
      }
      var viewLink = this.querySelector('[data-rs-led-view-product]');
      if (viewLink) { viewLink.hidden = !driver.url; if (driver.url) viewLink.href = driver.url; }
      var addForm = this.querySelector('[data-rs-led-add-form]');
      if (addForm) {
        var canBuy = Boolean(driver.variantId && driver.available);
        addForm.hidden = !canBuy;
        addForm.dataset.variantId = canBuy ? driver.variantId : '';
      }
      var unavailable = this.querySelector('[data-rs-led-unavailable]');
      if (unavailable) unavailable.hidden = Boolean(driver.url && driver.variantId && driver.available);
    }

    setText(selector, value) {
      var element = this.querySelector(selector);
      if (element) element.textContent = value;
    }
  }

  if (!customElements.get('rs-led-driver-calculator')) customElements.define('rs-led-driver-calculator', RSLedDriverCalculatorElement);
})(typeof window !== 'undefined' ? window : globalThis);
